import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./Audits.module.scss";
import Table from "../../components/Table/Table";
import MultiSelect from "../../components/MultiSelect/MultiSelect";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker, { registerLocale } from "react-datepicker";
import { enGB } from "date-fns/locale";
import { format } from "date-fns";
import Input from "../../components/Input/Input";
import classNames from "classnames";
import Select from "../../components/Select/Select";
import { showModal } from "../../redux/modalStore";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import { ReactComponent as SearchIcon } from "../../icons/search.svg";
import PageLoader from "../../components/Loader/PageLoader";
import { AUDITS_LOADER } from "../../constants/loader";
import { formatDate, getLinkedData, handleExport } from "../../utils/helpers";
import _ from "lodash";
import { setLinkedData } from "../../redux/organizationStore";
import DateRangeFilter from "../../components/DateRangeFilter/DateFIlterRange";
import useFetchOrganizationData from "../../actions/useFetchOrganizationData";
import { Chart } from "chart.js/auto";
import {
  AlertTriangle,
  Archive,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Edit2,
  FileSearch,
  FileText,
  TrendingUp,
} from "lucide-react";
import axiosInstance from "../../utils/utils";
import { getOrganizationData } from "../../actions/organization";
import Swal from "sweetalert2";
import BulkEditModal from "../../modals/BulkEditModal/BulkEditModal";
import {
  clearResetSelection,
  resetSelection,
} from "../../redux/rowSelectionStore";
import ConfirmationPopup from "../../modals/ConfirmationPopup";
import ArchiveIllustration from "../../icons/archive-illustration.svg";
import { hideOverlay, showOverlay } from "../../redux/overlayLoaderStore";
import CalendarLinkPopup from "../../modals/CalendarLinkPopup/CalendarLinkPopup";
import { updateFloatingBarVisible } from "../../redux/layoutStore";
import { setMeMode } from "../../redux/filterStore";
import { useTranslation } from "react-i18next";

const CLOSED_STATUSES = ["done", "closed"];
const STATUS_ORDER = ["Open", "In Progress", "In Review", "Done"];
const STATUS_COLORS = {
  Open: "#C5C5C5",
  "In Progress": "#FF7427",
  "In Review": "#AD88C6",
  Done: "#16A249",
};
const DASHBOARD_PAGE_SIZE = 5;

registerLocale("en-GB", enGB);

const isClosedStatus = (status) =>
  CLOSED_STATUSES.includes(status?.toLowerCase?.());

const getCompletionDate = (task) => {
  if (!Array.isArray(task?.updates)) return null;
  const doneUpdate = [...task.updates].reverse().find((update) => {
    const body = update?.body?.toString?.().toLowerCase?.() ?? "";
    return body.includes("status: done") || body.includes("status: closed");
  });
  return doneUpdate ? getSafeDate(doneUpdate.date) : null;
};

const getDateKey = (date) => {
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
};

const getSafeDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const StatusDonutChart = React.memo(function StatusDonutChart({
  labels,
  values,
  colors,
}) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!canvasRef.current) return;
    const total = values.reduce((sum, value) => sum + value, 0);
    if (!total) return;

    chartRef.current = new Chart(canvasRef.current, {
      type: "doughnut",
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderColor: "#FFFFFF",
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              padding: 12,
              color: "#1F2937",
              font: {
                size: 13,
                weight: "400",
              },
            },
          },
          tooltip: {
            callbacks: {
              label: (context) => `${context.label}: ${context.raw}`,
            },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [colors, labels, values]);

  return (
    <div className={styles.statusChartCanvas}>
      <canvas ref={canvasRef} />
    </div>
  );
});

const MonthlyBarChart = React.memo(function MonthlyBarChart({
  labels,
  values,
  label,
}) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!canvasRef.current) return;
    if (!labels.length) return;

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label,
            data: values,
            backgroundColor: "#5CA7CD",
            borderRadius: 6,
            maxBarThickness: 32,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            grid: {
              display: false,
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [label, labels, values]);

  return (
    <div className={styles.barChartCanvas}>
      <canvas ref={canvasRef} />
    </div>
  );
});

const Audits = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionToken = localStorage.getItem("session_token");
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "all" ? "all" : "overview";

  const [selectedChapters, setSelectedChapters] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedResponsibles, setSelectedResponsibles] = useState([]);
  const [selectedFrequencies, setSelectedFrequencies] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [searchQuery, setSearchQuery] = useState("");
  const [dashboardSearch, setDashboardSearch] = useState("");
  const [dashboardStatus, setDashboardStatus] = useState("all");
  const [dashboardPage, setDashboardPage] = useState(1);
  const [selectedAudits, setSelectedAudits] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState("");
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const [actionToast, setActionToast] = useState(null);
  const actionToastTimerRef = useRef(null);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(
    () => new Date(),
  );
  const { loadingContexts } = useSelector((state) => state.loader);
  const modules = useSelector((state) => state.user.modules[orgId]);
  const { meModeActive } = useSelector((state) => state.filters);
  const currentUser = useSelector((state) => state.user.email);
  const { auditsTasksData, data } = useSelector((state) => state.organization);
  const resetRowSelection = useSelector(
    (state) => state.rowSelection.resetSelection,
  );

  console.log(auditsTasksData, 'auditsTasksData');

  useEffect(() => {
    return () => {
      if (actionToastTimerRef.current) {
        window.clearTimeout(actionToastTimerRef.current);
        actionToastTimerRef.current = null;
      }
    };
  }, []);

  console.log(auditsTasksData);
  
  const isLoading = loadingContexts[AUDITS_LOADER];

  const baseUrl = process.env.REACT_APP_API_BASE;
  const calendarFeedLink = `${baseUrl}/feed/audit_tasks.ical?session_token=${sessionToken}&org_id=${orgId}`;

  const allSelectedAudits = selectedAudits;

  useFetchOrganizationData();

  // UseEffect for enabling/disabling me mode when navigating through kanban columns links
  useEffect(() => {
    if (location.state?.fromKanban) {
      dispatch(setMeMode(true));
    }

    return () => {
      if (location.state?.fromKanban) {
        dispatch(setMeMode(false));
      }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawStatuses = params.getAll("status");

    const statusesFromUrl = rawStatuses.every((s) => !s.includes(","))
      ? rawStatuses
      : [];

    setSelectedStatuses(statusesFromUrl);
  }, [location.search]);

  const handleTabChange = (nextTab) => {
    const nextParams = new URLSearchParams(location.search);
    if (nextTab === "overview") {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", "all");
    }

    navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true });
  };

  const auditTaskItems = useMemo(
    () => Object.values(auditsTasksData || {}),
    [auditsTasksData],
  );

  const auditDashboard = useMemo(() => {
    const visibleTasks = auditTaskItems.filter((task) => !task.archived);
    const total = visibleTasks.length;
    const completed = visibleTasks.filter((task) =>
      isClosedStatus(task.status),
    );
    const open = visibleTasks.filter((task) => !isClosedStatus(task.status));

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const overdue = visibleTasks.filter((task) => {
      const dueDate = getSafeDate(task.date);
      return dueDate && dueDate < today && !isClosedStatus(task.status);
    });

    const completedWithDates = completed.map((task) => ({
      dueDate: getSafeDate(task.date),
      completionDate: getCompletionDate(task),
    }));
    const comparableCompleted = completedWithDates.filter(
      (task) => task.completionDate,
    );
    const onTimeCompleted = comparableCompleted.filter(
      ({ dueDate, completionDate }) => {
        if (!dueDate) return true;
        const dueDateEnd = new Date(dueDate);
        dueDateEnd.setHours(23, 59, 59, 999);
        return completionDate <= dueDateEnd;
      },
    );
    const onTimeRate = comparableCompleted.length
      ? Math.round((onTimeCompleted.length / comparableCompleted.length) * 100)
      : 0;

    const statusCounts = visibleTasks.reduce((acc, task) => {
      const rawStatus =
        task.status === "Closed" ? "Done" : task.status || "Open";
      const status = STATUS_ORDER.includes(rawStatus) ? rawStatus : "Open";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const statusChart = {
      labels: STATUS_ORDER,
      values: STATUS_ORDER.map((status) => statusCounts[status] || 0),
      colors: STATUS_ORDER.map((status) => STATUS_COLORS[status]),
    };

    const monthCounts = visibleTasks.reduce((acc, task) => {
      const dueDate = getSafeDate(task.date);
      if (!dueDate) return acc;
      const key = `${dueDate.getFullYear()}-${String(
        dueDate.getMonth() + 1,
      ).padStart(2, "0")}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const monthKeys = Object.keys(monthCounts).sort().slice(-6);
    const monthlyChart = {
      labels: monthKeys.map((key) =>
        new Date(`${key}-01`).toLocaleDateString("en-US", {
          month: "short",
          year: "2-digit",
        }),
      ),
      values: monthKeys.map((key) => monthCounts[key]),
    };

    const upcomingWindow = new Date(today);
    upcomingWindow.setDate(upcomingWindow.getDate() + 30);

    const upcoming = visibleTasks
      .map((task) => ({ ...task, dueDate: getSafeDate(task.date) }))
      .filter(
        (task) =>
          task.dueDate &&
          task.dueDate >= today &&
          task.dueDate <= upcomingWindow,
      )
      .sort((a, b) => a.dueDate - b.dueDate)
      .slice(0, 6);

    const recent = visibleTasks
      .map((task) => ({ ...task, dueDate: getSafeDate(task.date) }))
      .filter((task) => task.dueDate)
      .sort((a, b) => b.dueDate - a.dueDate)
      .slice(0, 6);

    return {
      total,
      open: open.length,
      completed: completed.length,
      overdue: overdue.length,
      onTimeRate,
      statusChart,
      monthlyChart,
      upcoming,
      recent,
    };
  }, [auditTaskItems]);

  const statusFilterOptions = useMemo(() => {
    const statusSet = new Set(
      auditTaskItems.map((task) => task.status).filter(Boolean),
    );
    const orderedStatuses = STATUS_ORDER.filter((status) =>
      statusSet.has(status),
    );
    const extraStatuses = Array.from(statusSet).filter(
      (status) => !STATUS_ORDER.includes(status),
    );

    return [
      { id: "all", label: t("audits.dashboard.filters.allStatuses") },
      ...orderedStatuses.map((status) => ({ id: status, label: status })),
      ...extraStatuses.map((status) => ({ id: status, label: status })),
    ];
  }, [auditTaskItems, t]);

  const dashboardAudits = useMemo(() => {
    const query = dashboardSearch.trim().toLowerCase();

    return auditTaskItems
      .filter((task) => !task.archived)
      .filter((task) => {
        if (dashboardStatus === "all") return true;
        return task.status === dashboardStatus;
      })
      .filter((task) => {
        if (!query) return true;
        const responsibleValue = Array.isArray(task.responsible)
          ? task.responsible.join(" ")
          : task.responsible;
        return (
          task.key?.toLowerCase().includes(query) ||
          task.name?.toLowerCase().includes(query) ||
          task.type?.toLowerCase().includes(query) ||
          responsibleValue?.toLowerCase?.().includes(query)
        );
      });
  }, [auditTaskItems, dashboardSearch, dashboardStatus]);

  useEffect(() => {
    setDashboardPage(1);
  }, [dashboardSearch, dashboardStatus]);

  const totalDashboardPages = Math.max(
    1,
    Math.ceil(dashboardAudits.length / DASHBOARD_PAGE_SIZE),
  );
  const dashboardStartIndex = (dashboardPage - 1) * DASHBOARD_PAGE_SIZE;
  const dashboardEndIndex = dashboardStartIndex + DASHBOARD_PAGE_SIZE;
  const paginatedDashboardAudits = dashboardAudits.slice(
    dashboardStartIndex,
    dashboardEndIndex,
  );

  useEffect(() => {
    if (dashboardPage > totalDashboardPages) {
      setDashboardPage(totalDashboardPages);
    }
  }, [dashboardPage, totalDashboardPages]);

  const auditsByDate = useMemo(() => {
    const grouped = {};
    auditTaskItems
      .filter((task) => !task.archived)
      .forEach((task) => {
        const dueDate = getSafeDate(task.date);
        if (!dueDate) return;
        const key = getDateKey(dueDate);
        if (!key) return;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(task);
      });

    return grouped;
  }, [auditTaskItems]);

  const selectedCalendarKey = getDateKey(selectedCalendarDate);
  const auditsForSelectedDate = selectedCalendarKey
    ? auditsByDate[selectedCalendarKey] || []
    : [];

  // Extracting path from URL
  const lastIndex = location.pathname.lastIndexOf("/");
  const modifiedPath = location.pathname.substring(lastIndex);

  // Find the module object corresponding to the current path
  const currentModule = modules?.find((mod) => mod.path === modifiedPath);

  const uniqueChapters = auditTaskItems.length
    ? _.uniqBy(
      auditTaskItems.flatMap((issue) =>
        issue?.form?.map((form) => ({
          id: `${form?.risk?.qms_id}-${form?.risk?.qms_description}`,
          label: `${form?.risk?.qms_id} - ${form?.risk?.qms_description}`,
        })),
      ),
      "id",
    )
    : [];

  const handleChapterFilterChange = (newSelectedValues) => {
    setSelectedChapters(newSelectedValues);
  };

  const auditNames = auditTaskItems.length
    ? _.uniqBy(
      auditTaskItems.map((audit) => ({
        id: audit.name,
        label: audit.name,
      })),
      "id",
    )
    : [];

  const handleNameFilterChange = (newSelectedValues) => {
    setSelectedNames(newSelectedValues);
  };

  const uniqueStatuses = auditTaskItems.length
    ? _.uniqBy(
      auditTaskItems.map((issue) => ({
        id: issue.status,
        label: issue.status,
      })),
      "id",
    )
    : [];

  const handleStatusFilterChange = (newSelectedValues) => {
    setSelectedStatuses(newSelectedValues);

    const newSearchParams = new URLSearchParams(location.search);

    newSearchParams.delete("status");
    newSelectedValues.forEach((status) => {
      newSearchParams.append("status", status);
    });

    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  const uniqueResponsibles = auditTaskItems.length
    ? _.uniqBy(
      auditTaskItems.flatMap((issue) =>
        Array.isArray(issue.responsible)
          ? issue.responsible.map((res) => ({
            id: res,
            label: res,
          }))
          : issue.responsible
            ? [{ id: issue.responsible, label: issue.responsible }]
            : [],
      ),
      "id",
    )
    : [];

  const handleResponsibleFilterChange = (newSelectedValues) => {
    setSelectedResponsibles(newSelectedValues);
  };

  const uniqueFrequencies = auditTaskItems.length
    ? _.uniqBy(
      auditTaskItems
        .filter((item) => item.frequency)
        .map((item) => ({
          id: item.frequency,
          label:
            item.frequency.charAt(0).toUpperCase() + item.frequency.slice(1),
        })),
      "id",
    )
    : [];

  const handleFrequencyFilterChange = (newSelectedValues) => {
    setSelectedFrequencies(newSelectedValues);
  };

  const handleSelectedRowsChange = (selectedRows) => {
    setSelectedAudits(selectedRows);
  };

  const typeOptions = [
    { id: "internal", label: t("audits.filters.typeInternal") },
    { id: "external", label: t("audits.filters.typeExternal") },
  ];

  const handleTypeFilterChange = (newSelectedValues) => {
    setSelectedTypes(newSelectedValues);
  };

  const handleExportSelectedAudits = async () => {
    setLoading(true);
    setLoadingAction("export");

    await handleExport({
      selectedItems: allSelectedAudits,
      orgId,
      sessionToken,
      getKey: (item) => item.key,
      filePrefix: "Audit",
    });

    setLoading(false);
    setLoadingAction("");
  };

  const handleBulkArchive = async () => {
    setShowConfirmationPopup(false);

    if (!allSelectedAudits.length) return;

    const payload = allSelectedAudits.map((project) => ({
      id: project.id,
      archived: true,
    }));

    const archivedKeys = allSelectedAudits.map((project) => project.key);

    setLoading(true);
    setLoadingAction("archive");
    dispatch(showOverlay());

    try {
      await axiosInstance.patch(
        `/nocodb/audit_tasks?org_id=${orgId}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "session-token": sessionToken,
          },
        },
      );

      // Dispatch updated organization data
      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          false,
          false,
          true,
          false,
          false,
        ),
      );

      // Show success alert
      Swal.fire({
        toast: true,
        icon: "success",
        title: `Archive successful`,
        text: `Keys archived: ${archivedKeys.join(", ")}`,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire({
        toast: true,
        icon: "error",
        title: `Archive failed`,
        text: `Could not archive audit tasks. Please try again.`,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setLoading(false);
      setLoadingAction("");
      dispatch(hideOverlay()); // Hide overlay

      setSelectedAudits([]);

      // Trigger row selection reset
      dispatch(resetSelection());
      setTimeout(() => dispatch(clearResetSelection()), 100); // Reset to false after a short delay
    }
  };

  const handleBulkEdit = () => {
    dispatch(
      showModal({
        name: "bulk_edit_modal",
        data: allSelectedAudits,
      }),
    );
  };

  const showActionToast = (toast) => {
    setActionToast(toast);
    if (actionToastTimerRef.current) {
      window.clearTimeout(actionToastTimerRef.current);
    }
    actionToastTimerRef.current = window.setTimeout(() => {
      setActionToast(null);
      actionToastTimerRef.current = null;
    }, 4000);
  };

  const handleBulkEditClick = () => {
    if (allSelectedAudits.length === 0) {
      showActionToast({
        title: t("audits.actionToast.noneSelectedTitle"),
        text: t("audits.actionToast.bulkEditNoneSelectedText"),
      });
      return;
    }
    if (allSelectedAudits.length < 2) {
      showActionToast({
        title: t("audits.actionToast.notEnoughSelectedTitle"),
        text: t("audits.actionToast.bulkEditMinSelectedText"),
      });
      return;
    }
    handleBulkEdit();
  };

  const handleArchiveClick = () => {
    if (allSelectedAudits.length === 0) {
      showActionToast({
        title: t("audits.actionToast.noneSelectedTitle"),
        text: t("audits.actionToast.archiveNoneSelectedText"),
      });
      return;
    }
    setShowConfirmationPopup(true);
  };

  const handleExportClick = () => {
    if (allSelectedAudits.length === 0) {
      showActionToast({
        title: t("audits.actionToast.noneSelectedTitle"),
        text: t("audits.actionToast.exportNoneSelectedText"),
      });
      return;
    }
    handleExportSelectedAudits();
  };

  const allFilteredAudits = auditTaskItems
    .filter((item) => !item.archived)
    .filter((item) => {
        const {
          date,
          form,
          status,
          responsible,
          accountable,
          consulted,
          informed,
          key,
          name,
          frequency,
        } = item;

        const dueDate = new Date(date);
        const formattedStartDate = dateRange[0];
        const formattedEndDate = dateRange[1];

        const searchQueryLower = searchQuery.toLowerCase();

        const filters = [
          selectedChapters.length === 0 ||
          form.some((formItem) =>
            selectedChapters.includes(
              `${formItem.risk.qms_id}-${formItem.risk.qms_description}`,
            ),
          ),
          selectedNames.length === 0 || selectedNames.includes(name),
          selectedStatuses.length === 0 || selectedStatuses.includes(status),
          selectedResponsibles.length === 0 ||
          (Array.isArray(responsible)
            ? responsible.some((r) => selectedResponsibles.includes(r))
            : selectedResponsibles.includes(responsible)),
          selectedFrequencies.length === 0 ||
          selectedFrequencies.includes(frequency),
          selectedTypes.length === 0 ||
          selectedTypes.includes(item.type?.toLowerCase()),
          (!formattedStartDate && !formattedEndDate) || // No date range filter applied
          (dueDate >= formattedStartDate && dueDate <= formattedEndDate),
          !searchQueryLower || // If there's no search query, ignore this filter
          key?.toLowerCase().includes(searchQueryLower) ||
          name?.toLowerCase().includes(searchQueryLower) ||
          frequency?.toLowerCase().includes(searchQueryLower) ||
          (Array.isArray(responsible)
            ? responsible.some((r) =>
              r.toLowerCase().includes(searchQueryLower),
            )
            : responsible?.toLowerCase().includes(searchQueryLower)) ||
          form.some((formItem) =>
            `${formItem.risk.qms_id}-${formItem.risk.qms_description}`
              ?.toLowerCase()
              .includes(searchQueryLower),
          ),
          !meModeActive ||
          [responsible, accountable, consulted, informed].some((role) =>
            Array.isArray(role)
              ? role.includes(currentUser)
              : role === currentUser,
          ),
        ];

        return filters.every(Boolean);
    });

  const visibleAudits = allFilteredAudits;

  const handleRowClick = (row) => {
    const linkedData = getLinkedData(row.original, data);
    dispatch(setLinkedData(linkedData));

    navigate(`/${orgId}/audits/${row.original.key}`, { replace: false });

    dispatch(updateFloatingBarVisible(false));
  };

  const handleExportCalendar = () => {
    setShowCalendarPopup(true);
  };

  const getStatusTone = (status) => {
    switch (status) {
      case "Open":
        return "openStatus";
      case "In Progress":
        return "inProgressStatus";
      case "In Review":
        return "inReviewStatus";
      case "Done":
      case "Closed":
        return "resolvedStatus";
      default:
        return "openStatus";
    }
  };

  const columns = [
    {
      accessorKey: "select",
      header: ({ table }) => (
        <div
          className={styles.customCheckboxContainer}
          onClick={(e) => {
            e.stopPropagation(); // Stop click event propagation
            table.toggleAllPageRowsSelected(!table.getIsAllPageRowsSelected());
          }}
        >
          {table.getIsAllPageRowsSelected() ? (
            <div className={styles.checked} />
          ) : (
            <div className={styles.unchecked} />
          )}
        </div>
      ),
      size: 50,
      cell: ({ row }) => (
        <div className={styles.customCheckboxContainer}>
          {row.getIsSelected() ? (
            <div className={styles.checked} />
          ) : (
            <div className={styles.unchecked} />
          )}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "key",
      header: t("audits.table.key"),
      cell: (props) => (
        <span className={styles.keyPill}>{props.getValue()}</span>
      ),
      enableSorting: true,
      sortingFn: "alphanumeric",
    },
    {
      accessorKey: "name",
      header: t("audits.table.name"),
      size: 250,
      cell: (props) => <div>{props.getValue()}</div>,
      enableSorting: false,
    },
    {
      accessorKey: "responsible",
      header: t("audits.table.responsible"),
      size: 170,
      cell: (props) => {
        const responsible = props.row.original.responsible;
        const hasResponsible = Array.isArray(responsible)
          ? responsible.length > 0
          : Boolean(responsible);

        return <div>{hasResponsible ? t("audits.table.assigned") : ""}</div>;
      },
      enableSorting: false,
    },
    {
      accessorKey: "status",
      header: t("audits.table.status"),
      minSize: 90,
      cell: (props) => {
        const status = props.getValue();
        let statusClassName = "";

        switch (status) {
          case "Open":
            statusClassName = styles.openStatus;
            break;
          case "In Progress":
            statusClassName = styles.inProgressStatus;
            break;
          case "In Review":
            statusClassName = styles.inReviewStatus;
            break;
          case "Done":
            statusClassName = styles.resolvedStatus;
            break;
          default:
            break;
        }

        return (
          <div className={classNames(styles.statusCell, statusClassName)}>
            {status}
          </div>
        );
      },
      enableSorting: false,
      // sortingFn: (rowA, rowB, columnId) => {
      //   // Custom sorting function for status
      //   const statusA = rowA.getValue(columnId);
      //   const statusB = rowB.getValue(columnId);
      //
      //   // If one is "Done" and the other is not, "Done" goes to the bottom
      //   if (statusA === "Done" && statusB !== "Done") return 1;
      //   if (statusA !== "Done" && statusB === "Done") return -1;
      //
      //   // If both are the same status, maintain the original order
      //   return 0;
      // },
    },
    {
      accessorKey: "date",
      header: t("audits.table.dueDate"),
      cell: (props) => <div>{formatDate(props.getValue())}</div>,
      sortingFn: "datetime",
    },
    {
      accessorKey: "frequency",
      header: t("audits.table.frequency"),
      cell: (props) => (
        <div>
          {props.row.original?.frequency?.charAt(0).toUpperCase() +
            props.row.original?.frequency?.slice(1)}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "chapter",
      header: t("audits.table.chapter"),
      cell: (props) => {
        const uniqueRisks = _.uniqBy(
          props.row.original.form,
          (item) => `${item.risk?.qms_id} - ${item.risk?.qms_description}`,
        );
        return (
          <div>
            {uniqueRisks.map((item, index) => (
              <div key={index}>
                {item.risk?.qms_id} - {item.risk?.qms_description}
              </div>
            ))}
          </div>
        );
      },
      size: 250,
      enableSorting: false,
    },
  ];

  const selectedAuditsCount = allSelectedAudits.length;
  const hasSelectedAudits = selectedAuditsCount > 0;
  const selectedAuditsLabel =
    selectedAuditsCount === 1
      ? t("audits.selectionBar.single", { count: selectedAuditsCount })
      : t("audits.selectionBar.multiple", { count: selectedAuditsCount });
  const hasStatusData = auditDashboard.statusChart.values.some(
    (value) => value > 0,
  );
  const hasMonthlyData = auditDashboard.monthlyChart.labels.length > 0;

  const handleClearSelection = () => {
    setSelectedAudits([]);
    dispatch(resetSelection());
    setTimeout(() => dispatch(clearResetSelection()), 100);
  };

  return (
    <MainLayout>
      <BulkEditModal modalType="audits" orgId={orgId} />
      <div className={styles.column}>
        {isLoading && <PageLoader text={t("audits.loading")} />}
        {!isLoading && (
          <>
            <div className={styles.auditsContainer}>
              <div className={styles.pageHeader}>
                <div className={styles.titleGroup}>
                  <div className={styles.titleIcon}>
                    <FileSearch size={32} />
                  </div>
                  <div className={styles.pageTitle}>
                    {t("audits.title")}
                  </div>
                </div>
                <div className={styles.pageSubtitle}>
                  {t("audits.subtitle")}
                </div>
              </div>

              <div className={styles.tabsRow}>
                <div className={styles.tabSwitcher} role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "overview"}
                    className={classNames(styles.tabButton, {
                      [styles.tabButtonActive]: activeTab === "overview",
                    })}
                    onClick={() => handleTabChange("overview")}
                  >
                    {t("audits.tabs.overview")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "all"}
                    className={classNames(styles.tabButton, {
                      [styles.tabButtonActive]: activeTab === "all",
                    })}
                    onClick={() => handleTabChange("all")}
                  >
                    {t("audits.tabs.allAudits")}
                  </button>
                </div>
              </div>

              {activeTab === "overview" && (
                <>
                  <div className={styles.actionsRow}>
                    <button
                      className={styles.actionGhostButton}
                      onClick={handleBulkEditClick}
                      disabled={loading}
                      type="button"
                    >
                      <Edit2 size={16} />
                      {t("audits.actions.bulkEdit")}
                    </button>
                    <button
                      className={styles.actionGhostButton}
                      onClick={handleArchiveClick}
                      disabled={loading}
                      type="button"
                    >
                      <Archive size={16} />
                      {loading && loadingAction === "archive"
                        ? t("audits.actions.archiving")
                        : t("audits.actions.archive")}
                    </button>
                    <button
                      className={styles.actionGhostButton}
                      onClick={handleExportClick}
                      disabled={loading}
                      type="button"
                    >
                      <FileText size={16} />
                      {loading && loadingAction === "export"
                        ? t("audits.actions.exporting")
                        : t("audits.actions.export")}
                    </button>
                    <button
                      className={styles.actionGhostButton}
                      onClick={handleExportCalendar}
                      disabled={loading}
                      type="button"
                    >
                      <Calendar size={16} />
                      {t("audits.actions.calendarFeed")}
                    </button>
                  </div>

                  {actionToast && (
                    <div
                      className={styles.bottomToast}
                      role="alert"
                      aria-live="assertive"
                    >
                      <div className={styles.bottomToastTitle}>
                        {actionToast.title}
                      </div>
                      <div className={styles.bottomToastText}>
                        {actionToast.text}
                      </div>
                    </div>
                  )}

                  <div className={styles.filtersRow}>
                    <MultiSelect
                      label={
                        selectedNames?.length > 0
                          ? selectedNames.join(", ")
                          : t("audits.filters.audit")
                      }
                      options={auditNames}
                      onChangeSelectedValues={handleNameFilterChange}
                      selectedValues={selectedNames}
                      disabled={!auditTaskItems.length}
                      className={styles.multiselect}
                    />
                    <MultiSelect
                      label={
                        selectedResponsibles.length > 0
                          ? selectedResponsibles.join(", ")
                          : t("audits.filters.responsible")
                      }
                      options={uniqueResponsibles}
                      onChangeSelectedValues={handleResponsibleFilterChange}
                      selectedValues={selectedResponsibles}
                      disabled={!auditTaskItems.length}
                      className={styles.multiselect}
                    />
                    <MultiSelect
                      label={
                        selectedStatuses?.length > 0
                          ? selectedStatuses.join(", ")
                          : t("audits.filters.status")
                      }
                      options={uniqueStatuses}
                      onChangeSelectedValues={handleStatusFilterChange}
                      selectedValues={selectedStatuses}
                      disabled={!auditTaskItems.length}
                      className={styles.multiselect}
                    />
                    <MultiSelect
                      label={
                        selectedFrequencies.length > 0
                          ? selectedFrequencies.join(", ")
                          : t("audits.filters.frequency")
                      }
                      options={uniqueFrequencies}
                      onChangeSelectedValues={handleFrequencyFilterChange}
                      selectedValues={selectedFrequencies}
                      disabled={!auditTaskItems.length}
                      className={styles.multiselect}
                    />
                    <MultiSelect
                      label={
                        selectedChapters.length > 0
                          ? uniqueChapters
                            .filter((chapter) =>
                              selectedChapters.includes(chapter.id),
                            )
                            .map((chapter) => chapter.label)
                            .join(", ")
                          : t("audits.filters.chapter")
                      }
                      options={uniqueChapters}
                      onChangeSelectedValues={handleChapterFilterChange}
                      selectedValues={selectedChapters}
                      disabled={!auditTaskItems.length}
                      className={styles.multiselect}
                    />
                    <MultiSelect
                      label={
                        selectedTypes.length > 0
                          ? typeOptions
                            .filter((type) => selectedTypes.includes(type.id))
                            .map((type) => type.label)
                            .join(", ")
                          : t("audits.filters.type")
                      }
                      options={typeOptions}
                      onChangeSelectedValues={handleTypeFilterChange}
                      selectedValues={selectedTypes}
                      disabled={!auditTaskItems.length}
                      className={styles.multiselect}
                    />
                    <DateRangeFilter
                      handleDateRangeChange={setDateRange}
                      dateRange={dateRange}
                      isClearable
                      className={styles.auditDateFilter}
                    />
                    <Input
                      type="text"
                      placeholder={t("audits.filters.search")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={styles.input}
                      wrapperClassName={styles.searchWrapper}
                      icon={SearchIcon}
                    />
                  </div>

                  {hasSelectedAudits && (
                    <div className={styles.selectionBar}>
                      <div className={styles.selectionMeta}>
                        <span className={styles.selectionCount}>
                          {selectedAuditsLabel}
                        </span>
                        <button
                          type="button"
                          className={styles.selectionClearButton}
                          onClick={handleClearSelection}
                        >
                          {t("audits.selectionBar.clear")}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className={styles.tableStack}>
                    <div
                      className={classNames(
                        styles.tableCard,
                        !visibleAudits.length && styles.tableCardEmpty,
                      )}
                    >
                      <Table
                        columns={columns}
                        data={visibleAudits}
                        onRowClick={handleRowClick}
                        onSelectedRowsChange={handleSelectedRowsChange}
                        centered
                        rowHover
                        initialSorting={[{ id: "date", desc: false }]}
                        resetSelection={resetRowSelection}
                        variant="audits"
                      />
                      {!visibleAudits.length && (
                        <div className={styles.tableEmptyState}>
                          <div className={styles.tableEmptyContent}>
                            <div className={styles.tableEmptyCopy}>
                              {t("audits.noAudits")}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {activeTab === "all" && (
                <div className={styles.overviewSection}>
                  <div className={styles.metricGrid}>
                    <div className={styles.metricCard}>
                      <div className={styles.metricHeader}>
                        <span className={styles.metricTitle}>
                          {t("audits.dashboard.cards.total")}
                        </span>
                        <span
                          className={classNames(
                            styles.metricIconBadge,
                            styles.metricIconNeutral,
                          )}
                        >
                          <FileSearch size={18} />
                        </span>
                      </div>
                      <div className={styles.metricValue}>
                        {auditDashboard.total}
                      </div>
                    </div>

                    <div className={styles.metricCard}>
                      <div className={styles.metricHeader}>
                        <span className={styles.metricTitle}>
                          {t("audits.dashboard.cards.open")}
                        </span>
                        <span
                          className={classNames(
                            styles.metricIconBadge,
                            styles.metricIconActive,
                          )}
                        >
                          <Clock size={18} />
                        </span>
                      </div>
                      <div className={styles.metricValue}>
                        {auditDashboard.open}
                      </div>
                    </div>

                    <div className={styles.metricCard}>
                      <div className={styles.metricHeader}>
                        <span className={styles.metricTitle}>
                          {t("audits.dashboard.cards.completed")}
                        </span>
                        <span
                          className={classNames(
                            styles.metricIconBadge,
                            styles.metricIconSuccess,
                          )}
                        >
                          <CheckCircle2 size={18} />
                        </span>
                      </div>
                      <div className={styles.metricValue}>
                        {auditDashboard.completed}
                      </div>
                    </div>

                    <div className={styles.metricCard}>
                      <div className={styles.metricHeader}>
                        <span className={styles.metricTitle}>
                          {t("audits.dashboard.cards.overdue")}
                        </span>
                        <span
                          className={classNames(
                            styles.metricIconBadge,
                            styles.metricIconWarning,
                          )}
                        >
                          <AlertTriangle size={18} />
                        </span>
                      </div>
                      <div className={styles.metricValue}>
                        {auditDashboard.overdue}
                      </div>
                    </div>

                    <div className={styles.metricCard}>
                      <div className={styles.metricHeader}>
                        <span className={styles.metricTitle}>
                          {t("audits.dashboard.cards.onTimeRate")}
                        </span>
                        <span
                          className={classNames(
                            styles.metricIconBadge,
                            styles.metricIconNeutral,
                          )}
                        >
                          <TrendingUp size={18} />
                        </span>
                      </div>
                      <div className={styles.metricValue}>
                        {auditDashboard.onTimeRate}%
                      </div>
                    </div>
                  </div>

                  <div className={styles.chartsGrid}>
                    <div className={styles.chartCard}>
                      <div className={styles.chartHeader}>
                        {t("audits.dashboard.charts.status")}
                      </div>
                      {hasStatusData ? (
                        <div className={styles.statusChartBody}>
                          <StatusDonutChart
                            labels={auditDashboard.statusChart.labels}
                            values={auditDashboard.statusChart.values}
                            colors={auditDashboard.statusChart.colors}
                          />
                        </div>
                      ) : (
                        <div className={styles.chartEmptyState}>
                          {t("audits.dashboard.empty")}
                        </div>
                      )}
                    </div>

                    <div className={styles.chartCard}>
                      <div className={styles.chartHeader}>
                        {t("audits.dashboard.charts.monthly")}
                      </div>
                      {hasMonthlyData ? (
                        <div className={styles.statusChartBody}>
                          <MonthlyBarChart
                            labels={auditDashboard.monthlyChart.labels}
                            values={auditDashboard.monthlyChart.values}
                            label={t("audits.dashboard.charts.monthlyLabel")}
                          />
                        </div>
                      ) : (
                        <div className={styles.chartEmptyState}>
                          {t("audits.dashboard.empty")}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.auditBoard}>
                    <div className={styles.auditBoardMain}>
                      <div className={styles.auditBoardHeader}>
                        {t("audits.dashboard.lists.allAudits")}
                      </div>
                      <div className={styles.auditBoardFilters}>
                        <Input
                          type="text"
                          placeholder={t(
                            "audits.dashboard.filters.searchPlaceholder",
                          )}
                          value={dashboardSearch}
                          onChange={(e) => setDashboardSearch(e.target.value)}
                          className={styles.input}
                          wrapperClassName={styles.auditBoardSearch}
                          icon={SearchIcon}
                        />
                        <Select
                          text={t("audits.dashboard.filters.allStatuses")}
                          value={dashboardStatus}
                          onChange={setDashboardStatus}
                          options={statusFilterOptions}
                          variant="filters"
                          className={styles.auditBoardStatus}
                        />
                      </div>
                      <div className={styles.auditBoardList}>
                        {dashboardAudits.length ? (
                          <div className={styles.auditList}>
                            {paginatedDashboardAudits.map((audit) => (
                              <button
                                type="button"
                                key={audit.id || audit.key}
                                className={styles.auditListItem}
                                onClick={() =>
                                  handleRowClick({ original: audit })
                                }
                              >
                                <div className={styles.auditListMain}>
                                  <div className={styles.auditListTitleRow}>
                                    {audit.key && (
                                      <span className={styles.keyPill}>
                                        {audit.key}
                                      </span>
                                    )}
                                    <span className={styles.auditListTitle}>
                                      {audit.name ||
                                        t("audits.table.notDefined")}
                                    </span>
                                  </div>
                                  <div className={styles.auditListMeta}>
                                    {audit.type && (
                                      <span>
                                        {t("audits.dashboard.labels.type")}:{" "}
                                        {audit.type}
                                      </span>
                                    )}
                                    {audit.date && (
                                      <span>
                                        {t("audits.dashboard.labels.due")}:{" "}
                                        {formatDate(audit.date)}
                                      </span>
                                    )}
                                    {audit.responsible && (
                                      <span>
                                        {t("audits.dashboard.labels.responsible")}
                                        : {audit.responsible}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className={styles.auditListStatus}>
                                  <div
                                    className={classNames(
                                      "statusCell",
                                      getStatusTone(audit.status),
                                    )}
                                  >
                                    {audit.status ||
                                      t("audits.table.notDefined")}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className={styles.auditListEmpty}>
                            {t("audits.dashboard.lists.emptyFiltered")}
                          </div>
                        )}
                        {dashboardAudits.length > 0 && (
                          <div className={styles.paginationBar}>
                            <div className={styles.paginationInfo}>
                              {t("audits.dashboard.pagination.range", {
                                start: dashboardStartIndex + 1,
                                end: Math.min(
                                  dashboardEndIndex,
                                  dashboardAudits.length,
                                ),
                                total: dashboardAudits.length,
                              })}
                            </div>
                            <div className={styles.paginationControls}>
                              <button
                                type="button"
                                className={styles.paginationButton}
                                onClick={() =>
                                  setDashboardPage((prev) =>
                                    Math.max(1, prev - 1),
                                  )
                                }
                                disabled={dashboardPage === 1}
                              >
                                {t("audits.dashboard.pagination.previous")}
                              </button>
                              <button
                                type="button"
                                className={styles.paginationButton}
                                onClick={() =>
                                  setDashboardPage((prev) =>
                                    Math.min(totalDashboardPages, prev + 1),
                                  )
                                }
                                disabled={dashboardPage === totalDashboardPages}
                              >
                                {t("audits.dashboard.pagination.next")}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={styles.dashboardCard}>
                      <div className={styles.dashboardHeader}>
                        {t("audits.dashboard.calendar.title")}
                      </div>
                      <div className={styles.calendarBody}>
                        <DatePicker
                          inline
                          selected={selectedCalendarDate}
                          onChange={(date) => setSelectedCalendarDate(date)}
                          calendarClassName={styles.calendarPicker}
                          locale="en-GB"
                          renderCustomHeader={({
                            date,
                            decreaseMonth,
                            increaseMonth,
                            prevMonthButtonDisabled,
                            nextMonthButtonDisabled,
                          }) => (
                            <div className={styles.calendarHeader}>
                              <button
                                type="button"
                                onClick={decreaseMonth}
                                disabled={prevMonthButtonDisabled}
                                className={styles.calendarNavButton}
                                aria-label={t(
                                  "audits.dashboard.calendar.previousMonth",
                                  "Previous month",
                                )}
                              >
                                <ChevronLeft size={16} strokeWidth={1.8} />
                              </button>
                              <span className={styles.calendarMonthLabel}>
                                {format(date, "MMMM yyyy", { locale: enGB })}
                              </span>
                              <button
                                type="button"
                                onClick={increaseMonth}
                                disabled={nextMonthButtonDisabled}
                                className={styles.calendarNavButton}
                                aria-label={t(
                                  "audits.dashboard.calendar.nextMonth",
                                  "Next month",
                                )}
                              >
                                <ChevronRight size={16} strokeWidth={1.8} />
                              </button>
                            </div>
                          )}
                          dayClassName={(date) =>
                            auditsByDate[getDateKey(date)]
                              ? styles.calendarDayHasAudit
                              : undefined
                          }
                        />
                        <div className={styles.calendarList}>
                          <div className={styles.calendarListHeader}>
                            {selectedCalendarDate
                              ? formatDate(selectedCalendarDate)
                              : t("audits.dashboard.calendar.noDate")}
                          </div>
                          {auditsForSelectedDate.length ? (
                            <div className={styles.auditList}>
                              {auditsForSelectedDate.map((audit) => (
                                <button
                                  type="button"
                                  key={audit.id || audit.key}
                                  className={styles.auditListItem}
                                  onClick={() =>
                                    handleRowClick({ original: audit })
                                  }
                                >
                                  <div className={styles.auditListMain}>
                                    <div className={styles.auditListTitleRow}>
                                      {audit.key && (
                                        <span className={styles.keyPill}>
                                          {audit.key}
                                        </span>
                                      )}
                                      <span className={styles.auditListTitle}>
                                        {audit.name ||
                                          t("audits.table.notDefined")}
                                      </span>
                                    </div>
                                    <div className={styles.auditListMeta}>
                                      {audit.type && (
                                        <span>
                                          {t("audits.dashboard.labels.type")}:{" "}
                                          {audit.type}
                                        </span>
                                      )}
                                      {audit.responsible && (
                                        <span>
                                          {t(
                                            "audits.dashboard.labels.responsible",
                                          )}
                                          : {audit.responsible}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className={styles.auditListStatus}>
                                    <div
                                      className={classNames(
                                        "statusCell",
                                        getStatusTone(audit.status),
                                      )}
                                    >
                                      {audit.status ||
                                        t("audits.table.notDefined")}
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className={styles.auditListEmpty}>
                              {t("audits.dashboard.calendar.empty")}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.dashboardGrid}>
                    <div className={styles.dashboardCard}>
                      <div className={styles.dashboardHeader}>
                        {t("audits.dashboard.lists.upcoming")}
                      </div>
                      {auditDashboard.upcoming.length ? (
                        <div className={styles.auditList}>
                          {auditDashboard.upcoming.map((audit) => (
                            <button
                              type="button"
                              key={audit.id || audit.key}
                              className={styles.auditListItem}
                              onClick={() => handleRowClick({ original: audit })}
                            >
                              <div className={styles.auditListMain}>
                                <div className={styles.auditListTitleRow}>
                                  {audit.key && (
                                    <span className={styles.keyPill}>
                                      {audit.key}
                                    </span>
                                  )}
                                  <span className={styles.auditListTitle}>
                                    {audit.name || t("audits.table.notDefined")}
                                  </span>
                                </div>
                                <div className={styles.auditListMeta}>
                                  {audit.type && (
                                    <span>
                                      {t("audits.dashboard.labels.type")}:{" "}
                                      {audit.type}
                                    </span>
                                  )}
                                  {audit.date && (
                                    <span>
                                      {t("audits.dashboard.labels.due")}:{" "}
                                      {formatDate(audit.date)}
                                    </span>
                                  )}
                                  {audit.responsible && (
                                    <span>
                                      {t("audits.dashboard.labels.responsible")}
                                      : {audit.responsible}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className={styles.auditListStatus}>
                                <div
                                  className={classNames(
                                    "statusCell",
                                    getStatusTone(audit.status),
                                  )}
                                >
                                  {audit.status || t("audits.table.notDefined")}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className={styles.auditListEmpty}>
                          {t("audits.dashboard.lists.emptyUpcoming")}
                        </div>
                      )}
                    </div>

                    <div className={styles.dashboardCard}>
                      <div className={styles.dashboardHeader}>
                        {t("audits.dashboard.lists.recent")}
                      </div>
                      {auditDashboard.recent.length ? (
                        <div className={styles.auditList}>
                          {auditDashboard.recent.map((audit) => (
                            <button
                              type="button"
                              key={audit.id || audit.key}
                              className={styles.auditListItem}
                              onClick={() => handleRowClick({ original: audit })}
                            >
                              <div className={styles.auditListMain}>
                                <div className={styles.auditListTitleRow}>
                                  {audit.key && (
                                    <span className={styles.keyPill}>
                                      {audit.key}
                                    </span>
                                  )}
                                  <span className={styles.auditListTitle}>
                                    {audit.name || t("audits.table.notDefined")}
                                  </span>
                                </div>
                                <div className={styles.auditListMeta}>
                                  {audit.type && (
                                    <span>
                                      {t("audits.dashboard.labels.type")}:{" "}
                                      {audit.type}
                                    </span>
                                  )}
                                  {audit.date && (
                                    <span>
                                      {t("audits.dashboard.labels.due")}:{" "}
                                      {formatDate(audit.date)}
                                    </span>
                                  )}
                                  {audit.responsible && (
                                    <span>
                                      {t("audits.dashboard.labels.responsible")}
                                      : {audit.responsible}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className={styles.auditListStatus}>
                                <div
                                  className={classNames(
                                    "statusCell",
                                    getStatusTone(audit.status),
                                  )}
                                >
                                  {audit.status || t("audits.table.notDefined")}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className={styles.auditListEmpty}>
                          {t("audits.dashboard.lists.emptyRecent")}
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {showConfirmationPopup && (
        <ConfirmationPopup
          message={t("audits.confirmation.archiveMessage")}
          illustration={ArchiveIllustration}
          confirmText={t("audits.confirmation.archiveConfirm")}
          onConfirm={handleBulkArchive}
          onCancel={() => setShowConfirmationPopup(false)}
        />
      )}
      {showCalendarPopup && (
        <CalendarLinkPopup
          link={calendarFeedLink}
          onClose={() => setShowCalendarPopup(false)}
        />
      )}
    </MainLayout>
  );
};

export default Audits;
